import { html, View } from 'rune-ts';

export type GameHeaderData = {
  currentPlayerNickname: string;
  isMyTurn: boolean;
  deckCount: number;
  poolCount: number;
  turnDeadline: number | null;
  onLeaveClick?: () => void;
};

const TIMER_TICK_MS = 200;
const WARNING_THRESHOLD_SEC = 5;
const CAUTION_THRESHOLD_SEC = 10;

type TimerLevel = 'safe' | 'caution' | 'warning';

export class GameHeaderView extends View<GameHeaderData> {
  private tickHandle: number | null = null;
  private lastLevel: TimerLevel | null = null;
  private lastRemaining: number | null = null;

  override template() {
    const { currentPlayerNickname, isMyTurn, deckCount, poolCount, turnDeadline } = this.data;
    const turnLabel = isMyTurn ? '내 차례' : `${currentPlayerNickname} 차례`;
    const remaining = computeRemainingSec(turnDeadline);
    const showTimer = remaining !== null;
    const level = levelFor(remaining);
    const pillCls = isMyTurn
      ? 'bg-card-navy text-card-gold'
      : 'bg-wood-paper text-wood-coffee ring-1 ring-wood-sand';
    return html`
      <header class="game-header -mx-3 sm:-mx-4 -mt-3 sm:-mt-4 mlandscape:-mx-2 mlandscape:-mt-2 flex items-center justify-between gap-3 bg-wood-sand border-b border-wood-bark/30 px-5 py-2.5 sm:px-6 sm:py-3 mlandscape:px-3 mlandscape:py-1.5">
        <div class="flex items-center gap-2 sm:gap-3 min-w-0">
          <span class="text-subhead mlandscape:text-body font-serif italic text-wood-ink whitespace-nowrap select-none">
            Comple<span class="text-card-brass">tto</span>
          </span>
          <span class="text-caption text-wood-coffee/70 whitespace-nowrap font-mono tabular-nums hidden sm:inline-flex mlandscape:hidden">
            <span class="text-wood-coffee/55 mr-0.5 font-sans">덱</span>${deckCount}
            <span class="text-wood-sand mx-1">·</span>
            <span class="text-wood-coffee/55 mr-0.5 font-sans">풀</span>${poolCount}
          </span>
          <span class="rounded-full px-2.5 py-0.5 text-caption font-bold whitespace-nowrap ${pillCls}">
            ${turnLabel}
          </span>
          ${
            showTimer
              ? html`
                <span
                  data-role="turn-timer"
                  class="turn-timer-chip ${timerChipCls(level, isMyTurn)}"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="w-3.5 h-3.5 mlandscape:w-3 mlandscape:h-3 shrink-0"
                  >
                    <circle cx="12" cy="13" r="8"></circle>
                    <path d="M12 9v4l2.5 2"></path>
                    <path d="M9 2h6"></path>
                  </svg>
                  <span data-role="turn-timer-num" class="tabular-nums leading-none">${remaining}</span>
                  <span class="text-[10px] mlandscape:text-[9px] opacity-70 leading-none">s</span>
                </span>
              `
              : html``
          }
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            data-role="leave"
            aria-label="게임 종료"
            class="inline-flex items-center rounded-lg px-2 py-1 text-wood-coffee/70 hover:text-wood-ink hover:bg-wood-cream ring-1 ring-transparent hover:ring-wood-sand transition"
          >
            <span aria-hidden="true">⏏</span>
            <span class="hidden sm:inline mlandscape:hidden ml-1 text-caption font-medium">종료</span>
          </button>
        </div>
      </header>
    `;
  }

  protected override onMount(): void {
    const root = this.element();
    const btn = root.querySelector<HTMLButtonElement>('[data-role="leave"]');
    btn?.addEventListener('click', () => {
      this.data.onLeaveClick?.();
    });
    this.lastLevel = levelFor(computeRemainingSec(this.data.turnDeadline));
    this.lastRemaining = computeRemainingSec(this.data.turnDeadline);
    this.startTimerTick();
  }

  protected override onUnmount(): void {
    this.stopTimerTick();
  }

  private startTimerTick(): void {
    if (this.tickHandle !== null) return;
    if (this.data.turnDeadline === null) return;
    this.tickHandle = window.setInterval(() => this.updateTimerText(), TIMER_TICK_MS);
  }

  private stopTimerTick(): void {
    if (this.tickHandle !== null) {
      window.clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }

  private updateTimerText(): void {
    const root = this.element();
    if (!root.isConnected) {
      this.stopTimerTick();
      return;
    }
    const chipEl = root.querySelector<HTMLElement>('[data-role="turn-timer"]');
    const numEl = root.querySelector<HTMLElement>('[data-role="turn-timer-num"]');
    if (!chipEl || !numEl) return;
    const remaining = computeRemainingSec(this.data.turnDeadline);
    if (remaining === null) {
      numEl.textContent = '';
      return;
    }
    if (remaining !== this.lastRemaining) {
      numEl.textContent = `${remaining}`;
      this.lastRemaining = remaining;
    }
    const level = levelFor(remaining);
    if (level !== this.lastLevel) {
      chipEl.className = `turn-timer-chip ${timerChipCls(level, this.data.isMyTurn)}`;
      this.lastLevel = level;
    }
  }
}

function computeRemainingSec(deadline: number | null): number | null {
  if (deadline === null) return null;
  const ms = deadline - Date.now();
  return Math.max(0, Math.ceil(ms / 1000));
}

function levelFor(remaining: number | null): TimerLevel {
  if (remaining === null) return 'safe';
  if (remaining <= WARNING_THRESHOLD_SEC) return 'warning';
  if (remaining <= CAUTION_THRESHOLD_SEC) return 'caution';
  return 'safe';
}

function timerChipCls(level: TimerLevel, isMyTurn: boolean): string {
  const base =
    'inline-flex items-center gap-1 rounded-full px-2.5 py-1 mlandscape:px-2 mlandscape:py-0.5 text-body mlandscape:text-caption font-bold tabular-nums whitespace-nowrap ring-1 transition-colors';
  if (level === 'warning') {
    return `${base} bg-rose-500 text-white ring-rose-300 animate-pulse`;
  }
  if (level === 'caution') {
    return `${base} bg-amber-400 text-wood-ink ring-amber-500/60 shadow-sm`;
  }
  if (isMyTurn) {
    return `${base} bg-card-gold/20 text-wood-ink ring-card-gold/50`;
  }
  return `${base} bg-wood-paper text-wood-coffee ring-wood-sand`;
}
